import { readContract, signTypedData } from "wagmi/actions";
import { MAX_UINT_160, PERMIT_TWO_CONTRACT_ADDRESS } from "../config/consts";
import { Permit2Abi } from "./abis/permit2Abi";
import { Chain } from "viem/chains";
import { Permit1Details, Permit2Details, PERMIT_TWO_TYPES } from "./permitUtils";
import { BEBOP_CONTRACT } from "../store/global/TokenEnum";

interface GetPermit2NonceParams {
  userAddress: `0x${string}`;
  tokenAddress: `0x${string}`;
  spender: `0x${string}`;
}

export const getPermit2Nonce = async ({
  userAddress,
  tokenAddress,
  spender,
}: GetPermit2NonceParams) => {
  try {
    const res = await readContract({
      address: PERMIT_TWO_CONTRACT_ADDRESS,
      abi: Permit2Abi,
      functionName: "allowance",
      args: [userAddress, tokenAddress, spender],
    });
    const nonce = [...res].pop();
    console.log(`Got nonce for ${tokenAddress}: `, nonce);
    return nonce;
  } catch (err) {
    console.log(`Error getting nonce:`, err);
  }
};
export const getPermit2Domain = (chainId: number) => {
  return {
    name: "Permit2",
    chainId: Number(chainId),
    verifyingContract: PERMIT_TWO_CONTRACT_ADDRESS as `0x${string}`,
  } as const;
};

interface GetPermit2MessageParams {
  expiration: number;
  tokenAddresses: Array<`0x${string}`>;
  nonces: Array<number>;
}

export const getPermit2Message = ({
  expiration,
  tokenAddresses,
  nonces,
}: GetPermit2MessageParams) => {
  const details = tokenAddresses.map((tokenAddress, index) => {
    return {
      token: tokenAddress,
      amount: MAX_UINT_160,
      expiration: expiration,
      nonce: nonces[index],
    };
  });
  return {
    details: details,
    spender: BEBOP_CONTRACT as `0x${string}`,
    sigDeadline: BigInt(expiration),
  } as const;
};

export const getPermit2Signature = async (params: {
  userAddress: `0x${string}`;
  tokenAddresses: Array<`0x${string}`>;
  network: Chain;
  expiry: number;
}) => {
  const nonces = await Promise.all(
    params.tokenAddresses.map(tokenAddress => {
      return getPermit2Nonce({
        userAddress: params.userAddress,
        tokenAddress,
        spender: BEBOP_CONTRACT,
      }) as Promise<number>;
    })
  );
  if (nonces !== undefined && nonces !== null) {
    console.log(`Got nonces: `, nonces);
    const signature = await signTypedData({
      // @ts-expect-error ChainId needs to be a number not a BigInt (which is inferred from uint256)
      domain: getPermit2Domain(params.network.id),
      message: getPermit2Message({
        expiration: params.expiry,
        tokenAddresses: params.tokenAddresses,
        nonces,
      }),
      primaryType: "PermitBatch",
      types: PERMIT_TWO_TYPES,
    });
    return { signature, nonces };
  }
};

// Type narrower for use in other files
export const isPermit2Signature = (
  signature: Permit1Details | Permit2Details
): signature is Permit2Details => {
  return Array.isArray((signature as Permit2Details).nonces);
};
